/**
 * TutorMarkdown — Markdown renderer for tutor chat bubbles (assistant
 * messages only — see TutorMessageBubble.tsx).
 *
 * Reuses the SAME stack as packages/reader-ui/src/ReaderDocument.tsx
 * (react-markdown + remark-gfm + remark-math + rehype-katex — that file is
 * the canonical reference for this monorepo's Markdown/KaTeX setup) but
 * with its OWN, much lighter component set. ReaderDocument's styling (18px
 * serif reading type, 22px paragraph margins, heading demotion, `blockDomId`
 * anchors) is tuned for rendering a full manual page — none of that belongs
 * in a small chat bubble, so this is a fresh, chat-appropriate config rather
 * than a reuse of `MarkdownBody` itself.
 *
 * Sizing matches the rest of the bubble: `text-sm` here resolves to the same
 * 13px as `Text variant="body"` (see packages/design-system/src/styles/
 * tokens.css `--text-sm` + theme.css's Tailwind mapping), so markdown prose
 * and plain body text in this chat panel read at one consistent size.
 *
 * XSS: react-markdown never uses `dangerouslySetInnerHTML`, and no
 * `rehype-raw` plugin is installed — the model's own Markdown output cannot
 * inject raw HTML/scripts through this renderer.
 */

import * as React from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'

/** Stable plugin arrays — react-markdown uses reference equality for these;
 *  recreating them every render would defeat its internal memoization. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const REMARK_PLUGINS: any[] = [remarkGfm, remarkMath]
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const REHYPE_PLUGINS: any[] = [rehypeKatex]

/** A single compact heading treatment for h1-h6 — a chat answer has no real
 *  document hierarchy to preserve, so every heading level reads the same:
 *  a small bold label, not a title. */
function CompactHeading({ children }: { children?: React.ReactNode }) {
  return <p className="text-sm font-semibold text-ink mt-2 mb-1 first:mt-0">{children}</p>
}

const COMPONENTS: Components = {
  p: ({ children }) => (
    <p className="text-sm leading-relaxed text-ink-2 mb-2 last:mb-0">{children}</p>
  ),

  strong: ({ children }) => <strong className="font-semibold text-ink">{children}</strong>,
  em: ({ children }) => <em className="italic text-ink-2">{children}</em>,

  ul: ({ children }) => <ul className="mb-2 pl-5 list-disc text-ink-2 last:mb-0">{children}</ul>,
  ol: ({ children }) => <ol className="mb-2 pl-5 list-decimal text-ink-2 last:mb-0">{children}</ol>,
  li: ({ children }) => <li className="text-sm leading-relaxed my-0.5">{children}</li>,

  // Neutral border (not the accent used for citation marginalia below the
  // bubble) — keeps the accent colour reserved for "this links to the
  // manual", not diluted by unrelated blockquotes inside the answer itself.
  blockquote: ({ children }) => (
    <blockquote className="my-2 pl-3 border-l-2 border-line text-ink-2 italic">
      {children}
    </blockquote>
  ),

  pre: ({ children }) => (
    <pre className="my-2 px-3 py-2 overflow-x-auto border border-line rounded-md bg-surface text-ink-2 text-[13px]">
      {children}
    </pre>
  ),
  code: ({ children, className }) => {
    const isBlock = typeof className === 'string' && className.startsWith('language-')
    if (isBlock) {
      return (
        <code className={`font-mono text-[13px] text-ink-2 ${className ?? ''}`}>{children}</code>
      )
    }
    return (
      <code className="font-mono text-[13px] bg-bg-soft px-1 py-0.5 rounded text-ink">
        {children}
      </code>
    )
  },

  table: ({ children }) => (
    <div className="overflow-x-auto my-2 border border-line rounded-md bg-surface">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="bg-surface-2 text-ink-3 text-[10px] tracking-wide uppercase px-2 py-1.5 text-left">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-2 py-1.5 border-t border-line text-left align-top">{children}</td>
  ),

  hr: () => <hr className="my-3 border-none border-t border-line" />,

  a: ({ href, children }) => (
    <a
      href={href}
      target={href?.startsWith('#') ? undefined : '_blank'}
      rel={href?.startsWith('#') ? undefined : 'noopener noreferrer'}
      className="text-ar-accent underline underline-offset-2"
    >
      {children}
    </a>
  ),

  h1: CompactHeading,
  h2: CompactHeading,
  h3: CompactHeading,
  h4: CompactHeading,
  h5: CompactHeading,
  h6: CompactHeading,
}

export interface TutorMarkdownProps {
  content: string
}

export function TutorMarkdown({ content }: TutorMarkdownProps) {
  return (
    <ReactMarkdown
      remarkPlugins={REMARK_PLUGINS}
      rehypePlugins={REHYPE_PLUGINS}
      components={COMPONENTS}
    >
      {content}
    </ReactMarkdown>
  )
}

/**
 * TutorInlineMarkdown — TASK-012 REVISIONE 2 Parte B: renders a citation
 * "quote" (Tutor::CitationBuilder's markdown-balanced excerpt) INLINE only —
 * bold/italic/inline-code/math, never a block-level element. The marginalia
 * quote (TutorMessageBubble.tsx) is a single flowing, `line-clamp-3`-CSS-
 * truncated line of text; a `<p>`/list/heading landing in there would break
 * that layout, so every block tag react-markdown might produce is either
 * unwrapped to its inline children (`p`, via `disallowedElements` +
 * `unwrapDisallowed`) or dropped outright (`h1`-`h6`, lists, blockquote,
 * pre/table/hr/img — content this excerpt should never legitimately need).
 *
 * Reuses the SAME remark/rehype plugin stack as TutorMarkdown (remark-gfm +
 * remark-math/rehype-katex) — a source chunk can legitimately contain an
 * inline formula, and the module-level plugin arrays are already the stable
 * references react-markdown needs for its own memoization.
 */
const INLINE_DISALLOWED_ELEMENTS: string[] = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'blockquote',
  'pre',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'hr',
  'img',
]

const INLINE_COMPONENTS: Components = {
  // Unwrapped via disallowedElements/unwrapDisallowed below (kept here only
  // as a defensive second layer, matching react-markdown's documented
  // "unwrapDisallowed still runs its children through `components`" order).
  p: ({ children }) => <>{children}</>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  code: ({ children }) => (
    <code className="font-mono text-[12px] bg-bg-soft px-1 py-0.5 rounded">{children}</code>
  ),
  // No clickable links inside a short citation excerpt.
  a: ({ children }) => <>{children}</>,
}

export interface TutorInlineMarkdownProps {
  content: string
}

export function TutorInlineMarkdown({ content }: TutorInlineMarkdownProps) {
  return (
    <ReactMarkdown
      remarkPlugins={REMARK_PLUGINS}
      rehypePlugins={REHYPE_PLUGINS}
      components={INLINE_COMPONENTS}
      disallowedElements={INLINE_DISALLOWED_ELEMENTS}
      unwrapDisallowed
    >
      {content}
    </ReactMarkdown>
  )
}
